import React from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";

const TikTokIcon = ({ className = "w-5 h-5" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.01a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.44z" />
  </svg>
);

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-[#E5D8BD] bg-[#EFEBE6]/40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="font-serif text-2xl text-[#2D2926]">
            Colleen Prosser <span className="text-[#E25C46]">Art</span>
          </div>
          <p className="mt-4 text-sm text-[#5B5653] leading-relaxed max-w-sm">
            Original artworks by Colleen Prosser, an Indigenous Australian artist. Each painting
            carries a story from country, delivered free anywhere in Australia.
          </p>

          <div className="mt-6 flex items-center gap-4">
            <a
              href="https://www.instagram.com/prosser_colleen"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="Colleen Prosser on Instagram"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#E5D8BD] text-[#5B5653] hover:text-[#E25C46] hover:border-[#E25C46] transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://www.tiktok.com/@colleenart0"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-tiktok"
              aria-label="Colleen Prosser on TikTok"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#E5D8BD] text-[#5B5653] hover:text-[#E25C46] hover:border-[#E25C46] transition-colors"
            >
              <TikTokIcon className="w-5 h-5" />
            </a>
          </div>

          <p className="mt-6 text-xs text-[#5B5653]">Payments secured by Stripe</p>
        </div>

        <div>
          <p className="label-eyebrow-muted mb-4">Explore</p>
          <ul className="space-y-3 text-sm text-[#5B5653]">
            <li>
              <Link to="/" data-testid="footer-home" className="hover:text-[#E25C46]">
                Home
              </Link>
            </li>
            <li>
              <Link to="/store" data-testid="footer-store" className="hover:text-[#E25C46]">
                Store
              </Link>
            </li>
            <li>
              <Link to="/about" data-testid="footer-about" className="hover:text-[#E25C46]">
                About the Artist
              </Link>
            </li>
            <li>
              <Link to="/contact" data-testid="footer-contact" className="hover:text-[#E25C46]">
                Contact
              </Link>
            </li>
            <li>
              <Link
                to="/admin/login"
                data-testid="footer-artist-login"
                className="text-[#5B5653]/50 hover:text-[#5B5653] text-xs"
              >
                Artist login
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="label-eyebrow-muted mb-4">Information</p>
          <ul className="space-y-3 text-sm text-[#5B5653]">
            <li>
              <Link to="/terms" data-testid="footer-terms" className="hover:text-[#E25C46]">
                Terms &amp; Conditions
              </Link>
            </li>
            <li>
              <Link
                to="/refund-policy"
                data-testid="footer-refund"
                className="hover:text-[#E25C46]"
              >
                Refund Policy
              </Link>
            </li>
            <li>
              <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#E25C46]">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link
                to="/copyright"
                data-testid="footer-copyright"
                className="hover:text-[#E25C46]"
              >
                Copyright &amp; IP
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#E5D8BD]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 space-y-3 text-center text-xs text-[#5B5653]">
          <p
            data-testid="footer-acknowledgement"
            className="italic max-w-3xl mx-auto leading-relaxed"
          >
            We acknowledge the Traditional Custodians of the lands on which we live, create
            and trade, and pay our respects to Elders past and present.
          </p>
          <p>
            © {new Date().getFullYear()} Colleen Prosser. All artworks, photographs &amp;
            stories are the copyright and cultural property of the artist.
          </p>
        </div>
      </div>
    </footer>
  );
}
